'use client';

import React, { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { getTranslations } from '@/lib/i18n';

export default function HeroSection() {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <>
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16" suppressHydrationWarning>
        {/* Animated gradient background */}
        <div
          className="absolute inset-0 opacity-30 pointer-events-none animate-gradient-shift"
          style={{
            background: 'linear-gradient(135deg, #7C3AED, #F472B6, #22D3EE, #F59E0B, #7C3AED)',
            backgroundSize: '300% 300%',
            filter: 'blur(80px)',
          }}
        />

        {/* Background orbs — now with glow-pulse */}
        <div
          className="hero-glow-orb w-[500px] h-[500px] top-[-100px] left-[-150px] opacity-25 animate-glow-pulse"
          style={{ background: 'var(--primary)', animationDelay: '0s' }} />

        <div
          className="hero-glow-orb w-[400px] h-[400px] bottom-[-100px] right-[-100px] opacity-20 animate-glow-pulse"
          style={{ background: 'var(--accent)', animationDelay: '1.5s' }} />

        <div
          className="hero-glow-orb w-[300px] h-[300px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-15 animate-glow-pulse"
          style={{ background: 'var(--neon-pink)', animationDelay: '0.8s' }} />

        {/* Floating emoji decorations — richer animations */}
        <div className="absolute top-32 left-16 text-4xl animate-float-rotate hidden lg:block" style={{ animationDelay: '0s' }}>🎉</div>
        <div className="absolute top-48 right-24 text-3xl animate-float hidden lg:block" style={{ animationDelay: '0.8s' }}>🍺</div>
        <div className="absolute bottom-40 left-32 text-3xl animate-float-rotate hidden lg:block" style={{ animationDelay: '1.4s' }}>🎭</div>
        <div className="absolute bottom-32 right-32 text-4xl animate-float hidden lg:block" style={{ animationDelay: '0.4s' }}>🃏</div>
        <div className="absolute top-1/3 right-12 text-2xl animate-float-rotate hidden xl:block" style={{ animationDelay: '2s' }}>🔥</div>
        <div className="absolute top-1/4 left-1/4 text-2xl animate-float hidden xl:block" style={{ animationDelay: '1.2s' }}>⚡</div>
        <div className="absolute top-2/3 left-12 text-2xl animate-float-rotate hidden xl:block" style={{ animationDelay: '0.6s' }}>🎲</div>
        <div className="absolute top-1/2 right-1/4 text-xl animate-float hidden xl:block" style={{ animationDelay: '1.8s' }}>🌶️</div>

        <div className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16 text-center">
          {/* Badge — pop-in */}
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 animate-pop-in"
            style={{ background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)' }}
          >
            <Star size={12} className="animate-neon-flicker" style={{ color: 'var(--neon-amber)' }} />
            <span className="text-xs font-semibold shimmer-text">
              {t?.hero?.badge}
            </span>
            <Star size={12} className="animate-neon-flicker" style={{ color: 'var(--neon-amber)', animationDelay: '0.5s' }} />
          </div>

          {/* Headline — staggered slide-up */}
          <h1
            className="font-extrabold leading-tight mb-6 animate-slide-up"
            style={{ fontSize: 'clamp(2.5rem, 7vw, 5rem)', animationDelay: '0.1s' }}
          >
            <span className="text-gradient-party animate-neon-flicker">{t?.hero?.headline1}</span>
            <br />
            <span style={{ color: 'var(--foreground)' }}>{t?.hero?.headline2}</span>
          </h1>

          <p
            className="text-lg md:text-xl max-w-2xl mx-auto mb-12 leading-relaxed animate-slide-up"
            style={{ color: 'var(--muted-foreground)', animationDelay: '0.2s' }}
          >
            {t?.hero?.subtitle}
          </p>

          {/* Stats — staggered pop-in */}
          <div className="flex flex-wrap justify-center gap-8">
            {[
              { value: t?.hero?.stat1Value, label: t?.hero?.stat1Label, icon: '🎮', delay: '0.3s' },
              { value: t?.hero?.stat2Value, label: t?.hero?.stat2Label, icon: '🆓', delay: '0.45s' },
              { value: t?.hero?.stat3Value, label: t?.hero?.stat3Label, icon: '🔥', delay: '0.6s' },
            ]?.map((stat) => (
              <div
                key={`hero-stat-${stat?.label}`}
                className="text-center animate-pop-in"
                style={{ animationDelay: stat?.delay }}
              >
                <div className="text-2xl mb-1 animate-float" style={{ animationDelay: stat?.delay }}>{stat?.icon}</div>
                <div className="font-extrabold text-2xl" style={{ color: 'var(--foreground)' }}>{stat?.value}</div>
                <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{stat?.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{t?.hero?.scrollHint}</span>
          <div className="w-5 h-8 rounded-full border-2 flex justify-center pt-1.5" style={{ borderColor: 'var(--border)' }}>
            <div className="w-1 h-2 rounded-full animate-bounce" style={{ background: 'var(--primary)' }} />
          </div>
        </div>
      </section>
    </>
  );
}